import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, IconButton, Stack, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { UserAvatar } from '@/components/common';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useShiftSchedule, type ComputedShift } from '@/hooks/useShifts';
import { useClockTimes } from '@/hooks/useTimeTracking';
import type { ClockTime } from '@/types';
import { useStaffUsers, type StaffUser } from '@/hooks/useStaffUsers';
import {
  TimelineCalendar,
  TimelineItem,
  TimelineRow,
  TimelineRowGroup,
} from 'mq-timeline-calendar/react';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  setHours,
  format,
} from 'date-fns';
import { getBusinessHoursRange, hasWeekendHours } from '@/lib/inventoryUtils';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { SHIFT_CALENDAR_VIEW_KEY, useStoredCalendarView } from '@/hooks/useCalendarDefaultView';
import { ShiftItem } from './ShiftItem';
import { ShiftOverrideDialog } from './ShiftOverrideDialog';
import { calendarLocaleFor } from './supervisorTaskCalendar';
import { currentLanguage, dateFnsLocale } from '@/utils/locale';

const ROLE_ORDER: Record<string, number> = { mechanic: 0, manager: 1, admin: 2, owner: 3, reception: 4, developer: 5 };

const ROLE_HEADER_HEIGHT = 32;
const USER_ROW_HEIGHT = 60;

type UserRow = { type: 'user'; user: StaffUser; groupName: string };
type RoleHeaderRow = { type: 'roleHeader'; role: string };
type CalendarRow = UserRow | RoleHeaderRow;

interface ShiftCalendarTabProps {
  /** Fill the parent's available height (desktop full-bleed layout). */
  fillHeight?: boolean;
}

export function ShiftCalendarTab({ fillHeight = false }: ShiftCalendarTabProps) {
  const { t } = useTranslation('shifts');
  const { t: tcal } = useTranslation('calendar');
  const { t: ts } = useTranslation('settings');
  const themeMode = useUIStore((s) => s.themeMode);
  const isMobile = useIsMobile();
  const fill = fillHeight && !isMobile;
  const calendarHeight = fill
    ? '100%'
    : isMobile
      ? 'calc(100vh - 290px)'
      : 'calc(100vh - 370px)';
  const { calendarHours } = useTenantSettings();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Override mutations require shifts.manage server-side; without it the
  // calendar stays a read-only view (no override dialog on cell click).
  const canManageShifts = hasPermission('shifts.manage');

  const [currentDate, setCurrentDate] = useState(new Date());
  // Explicit pick (per tenant and user), else the workshop default (#4121).
  const { view, setView: handleViewChange } = useStoredCalendarView(SHIFT_CALENDAR_VIEW_KEY);
  const [selectedShift, setSelectedShift] = useState<ComputedShift | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const businessRange = useMemo(() => getBusinessHoursRange(calendarHours), [calendarHours]);
  const showWeekend = useMemo(() => hasWeekendHours(calendarHours), [calendarHours]);

  const { startDate, endDate } = useMemo(() => {
    switch (view) {
      case 'day':
        return {
          startDate: setHours(startOfDay(currentDate), businessRange.startHour),
          endDate: setHours(startOfDay(currentDate), businessRange.endHour),
        };
      case 'week': {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        return {
          startDate: weekStart,
          endDate: showWeekend
            ? endOfWeek(currentDate, { weekStartsOn: 1 })
            : endOfDay(addDays(weekStart, 4)),
        };
      }
      case 'month':
        return {
          startDate: startOfMonth(currentDate),
          endDate: endOfMonth(currentDate),
        };
    }
  }, [currentDate, view, businessRange, showWeekend]);

  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');

  const { data: shifts = [] } = useShiftSchedule({
    startDate: startStr,
    endDate: endStr,
  });

  const { data: clockTimes = [] } = useClockTimes(startStr, endStr);

  // Build lookup: "userId|date" → ClockTime
  const clockTimeMap = useMemo(() => {
    const map = new Map<string, ClockTime>();
    for (const ct of clockTimes) {
      map.set(`${ct.employeeId}|${ct.date}`, ct);
    }
    return map;
  }, [clockTimes]);

  const { data: staffData } = useStaffUsers({ isActive: true });
  const allStaff = useMemo(() => staffData?.users ?? [], [staffData]);

  // Build grouped calendar rows: role headers + user rows, sorted by role → group → name
  const calendarRows = useMemo(() => {
    const shiftUserIds = new Set(shifts.map((s) => s.userId));
    const usersWithShifts = allStaff.filter((u) => shiftUserIds.has(u.id));

    // Map userId → shift group name (take from first shift)
    const userGroupMap = new Map<string, string>();
    for (const s of shifts) {
      if (!userGroupMap.has(s.userId)) {
        userGroupMap.set(s.userId, s.groupName);
      }
    }

    // Sort: role order → group name → full name
    usersWithShifts.sort((a, b) => {
      const roleA = ROLE_ORDER[a.role] ?? 99;
      const roleB = ROLE_ORDER[b.role] ?? 99;
      if (roleA !== roleB) return roleA - roleB;

      const groupA = userGroupMap.get(a.id) ?? '';
      const groupB = userGroupMap.get(b.id) ?? '';
      if (groupA !== groupB) return groupA.localeCompare(groupB);

      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });

    // Build rows with role headers
    const rows: CalendarRow[] = [];
    let lastRole = '';
    for (const user of usersWithShifts) {
      if (user.role !== lastRole) {
        rows.push({ type: 'roleHeader', role: user.role });
        lastRole = user.role;
      }
      rows.push({ type: 'user', user, groupName: userGroupMap.get(user.id) ?? '' });
    }
    return rows;
  }, [shifts, allStaff]);

  const handlePrevious = useCallback(() => {
    setCurrentDate((d) => {
      switch (view) {
        case 'day': return subDays(d, 1);
        case 'week': return subWeeks(d, 1);
        case 'month': return subMonths(d, 1);
      }
    });
  }, [view]);

  const handleNext = useCallback(() => {
    setCurrentDate((d) => {
      switch (view) {
        case 'day': return addDays(d, 1);
        case 'week': return addWeeks(d, 1);
        case 'month': return addMonths(d, 1);
      }
    });
  }, [view]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleShiftClick = useCallback((shift: ComputedShift) => {
    if (!canManageShifts) return;
    setSelectedShift(shift);
    setOverrideOpen(true);
  }, [canManageShifts]);

  const dateLabel = useMemo(() => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE d.M.yyyy', { locale: dateFnsLocale() });
      case 'week':
        return `${format(startDate, 'd.M.', { locale: dateFnsLocale() })} - ${format(endDate, 'd.M.yyyy', { locale: dateFnsLocale() })}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: dateFnsLocale() });
    }
  }, [currentDate, startDate, endDate, view]);

  return (
    <Box
      className="os-timeline-root"
      sx={
        fill
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={tcal('previous')}>
            <IconButton onClick={handlePrevious} size="small" aria-label={tcal('previous')}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tcal('today')}>
            <IconButton onClick={handleToday} size="small" aria-label={tcal('today')}>
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tcal('next')}>
            <IconButton onClick={handleNext} size="small" aria-label={tcal('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ ml: 1 }}>
            {dateLabel}
          </Typography>
        </Stack>

        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => v && handleViewChange(v)}
          size="small"
        >
          <ToggleButton value="day">{t('day')}</ToggleButton>
          <ToggleButton value="week">{t('week')}</ToggleButton>
          <ToggleButton value="month">{t('month')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Calendar */}
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          ...(fill && { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
        }}
      >
          <TimelineCalendar
            startDate={startDate}
            endDate={endDate}
            height={calendarHeight}
            showCurrentTime={true}
            locale={calendarLocaleFor(currentLanguage())}
            theme={themeMode}
            sidebar={!isMobile && calendarRows.length > 1 ? {
              width: 140,
              content: (
                <>
                  {calendarRows.map((row) =>
                    row.type === 'roleHeader' ? (
                      <Box
                        key={`role-${row.role}`}
                        sx={{
                          height: ROLE_HEADER_HEIGHT,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" fontSize={10}>
                          {ts(`roles.${row.role}`)}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        key={row.user.id}
                        sx={{
                          height: 'var(--timeline-row-height, 60px)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          boxSizing: 'border-box',
                        }}
                      >
                        <UserAvatar
                          firstName={row.user.firstName}
                          lastName={row.user.lastName}
                          size={28}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={600} noWrap lineHeight={1.2}>
                            {row.user.firstName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap lineHeight={1.2} display="block">
                            {row.user.lastName}
                          </Typography>
                        </Box>
                      </Box>
                    ),
                  )}
                </>
              ),
            } : undefined}
          >
            <TimelineRowGroup>
              {calendarRows.map((row) =>
                row.type === 'roleHeader' ? (
                  <TimelineRow
                    key={`role-${row.role}`}
                    id={`role-${row.role}`}
                    label=""
                    rowCount={1}
                    height={ROLE_HEADER_HEIGHT}
                    collapsible={false}
                    style={{
                      borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                      background: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                    }}
                  />
                ) : (
                  <TimelineRow
                    key={row.user.id}
                    id={row.user.id}
                    label={isMobile ? `${row.user.firstName} ${row.user.lastName.charAt(0)}.` : `${row.user.firstName} ${row.user.lastName}`}
                    rowCount={1}
                    height={isMobile ? USER_ROW_HEIGHT : undefined}
                    collapsible={false}
                    style={{ borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` }}
                  >
                    {shifts
                      .filter((s) => s.userId === row.user.id)
                      .map((shift) => (
                        <TimelineItem
                          key={`${shift.userId}-${shift.date}-${shift.slotId}`}
                          startTime={`${shift.date}T${shift.startTime}`}
                          endTime={`${shift.date}T${shift.endTime}`}
                          row={0}
                          draggable={false}
                        >
                          <ShiftItem
                            shift={shift}
                            clockTime={clockTimeMap.get(`${shift.userId}|${shift.date}`)}
                            onClick={() => handleShiftClick(shift)}
                          />
                        </TimelineItem>
                      ))}
                  </TimelineRow>
                ),
              )}
            </TimelineRowGroup>
          </TimelineCalendar>
      </Paper>

      <ShiftOverrideDialog
        open={overrideOpen}
        onClose={() => {
          setOverrideOpen(false);
          setSelectedShift(null);
        }}
        shift={selectedShift}
      />
    </Box>
  );
}
